import { useCallback, useRef } from "react";
import { useCart } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";

/**
 * useDatabaseSync
 * ---------------
 * Compares UI state vs DB state and syncs the differences.
 *
 * KEY DESIGN: dbStateRef is updated SYNCHRONOUSLY at the top of each sync call
 * (before any await). This prevents concurrent sync calls from seeing the same
 * stale dbState and issuing duplicate API calls.
 */
export function useDatabaseSync() {

    const { project } = useProject();
    const { useCreate, useUpdate, useDelete } = useCart();

    // Instantiate mutations at hook level (React rules — can't call inside callbacks)
    const createSvc = useCreate();
    const updateSvc = useUpdate();
    const deleteSvc = useDelete();

    // Memory of what the database currently looks like
    const dbStateRef = useRef<Record<string, any>>({});


    // ─────────────────────────────────────────────────────────────────────────
    // STEP 1: Called once on page load.
    // Seeds our memory with the data that came from the database.
    // ─────────────────────────────────────────────────────────────────────────
    const initializeDbState = useCallback((dataFromDatabase: Record<string, any>) => {
        console.log("[DatabaseSync] Initialized with DB state:", dataFromDatabase);
        dbStateRef.current = { ...dataFromDatabase };
    }, []);


    // ─────────────────────────────────────────────────────────────────────────
    // STEP 2: Called every time selectedValues changes.
    // Finds what changed and syncs it.
    // ─────────────────────────────────────────────────────────────────────────
    const syncToDatabase = useCallback(async (uiState: Record<string, any>) => {
        const projectId = project?.id;
        if (!projectId) {
            console.warn("[DatabaseSync] No project ID found. Skipping sync.");
            return;
        }

        // ── Claim the new state IMMEDIATELY (before any await) ───────────────
        // Preserve any IDs that were already seeded from previous CREATE responses.
        // Without this, newly-created items (whose IDs aren't in uiState yet) would
        // lose their IDs in the claimed state, causing undefined DELETE calls.
        const dbState = dbStateRef.current;
        const claimedState: Record<string, any> = {};
        Object.entries(uiState).forEach(([category, item]: [string, any]) => {
            claimedState[category] = {
                ...item,
                id: dbState[category]?.id || item.id, // Preserve existing DB id if available
            };
        });
        dbStateRef.current = claimedState;


        // ── Compute what needs to change ─────────────────────────────────────
        const toCreate: any[] = [];
        const toUpdate: any[] = [];
        const toDelete: string[] = [];

        Object.entries(uiState).forEach(([category, uiItem]: [string, any]) => {
            const dbItem = dbState[category];
            const isComplete =
                !!uiItem.value &&
                !!uiItem.manufacturerProductId &&
                (uiItem.quantity || 0) > 0 &&
                !uiItem.awaitingColor; // Skip items waiting for color selection

            if (!isComplete) return;

            if (!dbItem) {
                toCreate.push({ category, ...uiItem });
            } else if (uiItem.manufacturerProductId !== dbItem.manufacturerProductId) {
                toDelete.push(dbItem.id);
                toCreate.push({ category, ...uiItem });
            } else if (uiItem.quantity !== dbItem.quantity) {
                toUpdate.push({ id: dbItem.id, category, quantity: uiItem.quantity });
            }
        });

        // Items in DB but gone from UI → DELETE
        Object.keys(dbState).forEach((category) => {
            if (!uiState[category] && dbState[category]?.id) {
                toDelete.push(dbState[category].id);
            }
        });

        const hasNoChanges = toCreate.length === 0 && toUpdate.length === 0 && toDelete.length === 0;
        if (hasNoChanges) {
            console.log("[DatabaseSync] No changes detected. Nothing to sync.");
            return;
        }

        console.group("[DatabaseSync] Sync Plan");

        // ── DELETES ───────────────────────────────────────────────────────────
        for (const id of toDelete) {
            if (!id || id.startsWith?.("pending_")) {
                console.warn(`  [DatabaseSync] Skipping DELETE — item not yet confirmed in DB (id: ${id})`);
                continue;
            }
            console.log(`  🗑️  DELETE id:`, id);
            try {
                await deleteSvc.mutateAsync({ Id: id });
            } catch (err) {
                console.error(`  [DatabaseSync] DELETE failed for id ${id}:`, err);
            }
        }


        // ── CREATES ───────────────────────────────────────────────────────────
        if (toCreate.length > 0) {
            console.log(`  ✅ CREATE (${toCreate.length} items):`, toCreate);
            try {
                const createdItems: any[] = await createSvc.mutateAsync({
                    ProjectId: projectId,
                    CartItems: toCreate.map((item) => ({
                        ManufacturerProductId: item.manufacturerProductId,
                        ItemCount: item.quantity,
                        ProductCategory: item.category,
                    })),
                });

                // Seed real IDs from the CREATE response into dbStateRef
                createdItems?.forEach((created: any) => {
                    if (created.ProductCategory && created.id) {
                        dbStateRef.current[created.ProductCategory] = {
                            ...uiState[created.ProductCategory],
                            id: created.id,
                        };
                    }
                });
            } catch (err) {
                console.error(`  [DatabaseSync] CREATE failed:`, err);
            }
        }

        // ── UPDATES ───────────────────────────────────────────────────────────
        for (const item of toUpdate) {
            console.log(`  🔄 UPDATE id:`, item.id, `quantity:`, item.quantity);
            try {
                await updateSvc.mutateAsync({ Id: item.id, ItemCount: item.quantity });
            } catch (err) {
                console.error(`  [DatabaseSync] UPDATE failed for id ${item.id}:`, err);
            }
        }

        console.groupEnd();

    }, [project?.id, createSvc, updateSvc, deleteSvc]);


    return {
        initializeDbState,
        syncToDatabase,
    };
}
